import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BrandMark from "@/components/BrandMark";
// Ambient audio is global (see /components/GlobalAmbientAudio.jsx)
import { useLocale } from "@/lib/locale";

export default function MomentView() {
    const { id } = useParams();
    const nav = useNavigate();
    const { t } = useLocale();
    const [moment, setMoment] = useState(null);
    const [err, setErr] = useState("");
    const [elapsed, setElapsed] = useState(0);
    const [isRetrying, setIsRetrying] = useState(false);
    const startedAtRef = useRef(Date.now());
    const pollStopRef = useRef({ stop: false });

    // Poll moment status every 5s. Exposed as a stable ref so the retry
    // handler can re-kick it after a /retry call flips status back to
    // generating — no remount gymnastics needed.
    const startPolling = () => {
        pollStopRef.current.stop = true;           // cancel any outstanding loop
        const token = { stop: false };
        pollStopRef.current = token;
        let tries = 0;
        const loop = async () => {
            if (token.stop) return;
            try {
                const { data } = await api.get(`/moments/${id}`);
                if (token.stop) return;
                setMoment(data);
                tries += 1;
                if (data.status === "ready" || data.status === "failed" || tries > 80) return;
                setTimeout(loop, 5000);
            } catch (e) {
                if (!token.stop) setErr(t("moment.err_generic"));
            }
        };
        loop();
    };

    useEffect(() => {
        startPolling();
        return () => { pollStopRef.current.stop = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Live elapsed-time counter (updates every second while generating).
    useEffect(() => {
        if (!moment) return;
        if (moment.status === "ready" || moment.status === "failed") return;
        const iv = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
        }, 1000);
        return () => clearInterval(iv);
    }, [moment]);

    const status = moment?.status || "pending_payment";

    // Prefer streamed URL (signed) over legacy base64 data URL.
    const videoSrc = moment?.video_url || moment?.video_data_url || null;
    const audioSrc = moment?.audio_url || moment?.audio_data_url || null;
    const thumbSrc = moment?.thumbnail_url || null;

    const videoRef = useRef(null);

    // Total ≈ Kling v1.6 Pro averages ~3-4 minutes for 5-10s video.
    // Render progress as a soft 0→95% curve capped well before 100%, never hits
    // 100 until the backend actually returns "ready". This keeps hope alive
    // without ever lying to the user.
    //
    // Progress also drives the 4-step timeline so the user sees the system
    // move through distinct phases (analysis → voice rebuild → creation →
    // finalization) instead of a featureless bar. Each step maps to a
    // progress band: 0-25% step1, 25-55% step2, 55-85% step3, 85-95% step4.
    const softProgress = Math.min(95, Math.round(95 * (1 - Math.exp(-elapsed / 120))));
    const activeStep =
        softProgress < 25 ? 0 :
        softProgress < 55 ? 1 :
        softProgress < 85 ? 2 : 3;

    // Rotating whisper — a short emotional line changes every ~12s so the
    // screen keeps breathing visually. 8 messages across the full wait give
    // the user new information at a pleasant cadence.
    // Memoized against elapsed/12 so React re-renders exactly once per whisper.
    const whisperKeys = useMemo(
        () => [
            "moment.gen_whisper_1", "moment.gen_whisper_2", "moment.gen_whisper_3",
            "moment.gen_whisper_4", "moment.gen_whisper_5", "moment.gen_whisper_6",
            "moment.gen_whisper_7", "moment.gen_whisper_8",
        ],
        [],
    );
    // Once progress crosses 75% of the soft cap, switch to a personalized
    // "she is almost here" whisper — if we know the presence's name we use
    // it ("Maria sta quasi arrivando…"), otherwise the neutral variant.
    // This compresses the final ~45s of the wait into the most intimate
    // moment of the entire experience.
    const presenceName = moment?.presence_name;
    const isFinalStretch = softProgress >= 75;
    const whisperIndex = Math.floor(elapsed / 12) % whisperKeys.length;
    const whisperText = isFinalStretch
        ? (presenceName
            ? t("moment.gen_whisper_near_named", { name: presenceName })
            : t("moment.gen_whisper_near"))
        : t(whisperKeys[whisperIndex]);

    const downloadVideo = () => {
        if (!videoSrc) return;
        const a = document.createElement("a");
        a.href = videoSrc;
        a.download = `other-side-moment-${id}.mp4`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    // Primary CTA on the ready screen: scroll the video into view and start
    // playback. If the browser blocks autoplay with sound, the <video>
    // controls remain available so the user can tap play manually.
    const watchNow = () => {
        const v = videoRef.current;
        if (!v) return;
        try {
            v.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch { /* older browsers */ }
        const p = v.play();
        if (p && typeof p.catch === "function") {
            p.catch(() => { /* autoplay blocked — controls handle it */ });
        }
    };

    // Retry handler for the soft-failed state. Calls the non-charging
    // retry endpoint, resets the elapsed timer, flips the UI back into
    // "generating" mode, and restarts polling so the user sees progress
    // immediately — no navigation, no re-charge, no wizard to redo.
    const retryMoment = async () => {
        if (isRetrying) return;
        setIsRetrying(true);
        setErr("");
        try {
            const { data } = await api.post(`/moments/${id}/retry`);
            startedAtRef.current = Date.now();
            setElapsed(0);
            // Optimistically flip local state so the user sees the
            // generating screen before the first poll returns.
            setMoment((prev) => ({
                ...(prev || {}),
                ...data,
                status: "generating",
                error: undefined,
            }));
            startPolling();
        } catch (e) {
            setErr(t("moment.err_generic"));
        } finally {
            setIsRetrying(false);
        }
    };

    return (
        <div className="grain min-h-screen candle-halo">
            <Header minimal />
            <main
                className="max-w-2xl mx-auto px-6 md:px-12 pt-16 pb-28 md:pb-32 text-center animate-fade-in"
                data-testid="moment-view-page"
            >
                <BrandMark size={40} className="mx-auto mb-8" ceremonial />

                {status === "pending_payment" && (
                    <>
                        <p className="label-eyebrow mb-6">{t("moment.eyebrow")}</p>
                        <h1 className="h-serif text-4xl md:text-5xl leading-tight">
                            {t("moment.status_pending")}
                        </h1>
                    </>
                )}

                {status === "generating" && (
                    <div data-testid="moment-generating">
                        {/* Ambient audio is handled globally by GlobalAmbientAudio,
                            mounted at the app root. Nothing to do here — the
                            engine follows the user across routes. */}

                        <p className="label-eyebrow text-ink-gold mb-6">{t("moment.eyebrow")}</p>
                        <h1 className="h-serif text-4xl md:text-5xl leading-tight">
                            {t("moment.status_generating")}
                        </h1>

                        {/* Rotating whisper — literally the emotional heartbeat of
                            the wait. Key changes every ~12s → React re-mounts the
                            node → CSS animation replays the gentle fade-in. */}
                        <p
                            key={whisperIndex}
                            className="mt-8 text-ink-textMid text-lg md:text-xl leading-relaxed font-serif italic max-w-md mx-auto animate-fade-in"
                            data-testid="moment-whisper"
                        >
                            {whisperText}
                        </p>

                        {/* ── Step timeline ──────────────────────────────
                            4 clearly named phases. Completed: gold fill + ✓.
                            Active: gold ring + pulsing dot. Pending: muted. */}
                        <ol
                            className="mt-14 flex items-stretch justify-between gap-2 md:gap-4 max-w-2xl mx-auto"
                            data-testid="moment-steps"
                            aria-label="progress steps"
                        >
                            {[
                                { key: "moment.gen_step1", i: 0 },
                                { key: "moment.gen_step2", i: 1 },
                                { key: "moment.gen_step3", i: 2 },
                                { key: "moment.gen_step4", i: 3 },
                            ].map(({ key, i }) => {
                                const state =
                                    i < activeStep ? "done" : i === activeStep ? "active" : "pending";
                                return (
                                    <li
                                        key={key}
                                        className="flex-1 flex flex-col items-center text-center"
                                        data-testid={`moment-step-${i + 1}`}
                                        data-state={state}
                                        aria-current={state === "active" ? "step" : undefined}
                                    >
                                        <span
                                            className={[
                                                "w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-sm md:text-base transition-all duration-500",
                                                state === "done" &&
                                                    "bg-ink-gold text-ink-deep shadow-[0_0_14px_rgba(232,192,125,0.35)]",
                                                state === "active" &&
                                                    "border-2 border-ink-gold text-ink-goldHi relative",
                                                state === "pending" &&
                                                    "border border-ink-border text-ink-textLow",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                        >
                                            {state === "done" ? (
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="3"
                                                    className="w-4 h-4"
                                                    aria-hidden="true"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            ) : state === "active" ? (
                                                <span className="w-2 h-2 rounded-full bg-ink-gold animate-pulse" />
                                            ) : (
                                                i + 1
                                            )}
                                            {state === "active" && (
                                                <span
                                                    className="absolute inset-0 rounded-full"
                                                    style={{
                                                        boxShadow:
                                                            "0 0 0 0 rgba(232,192,125,0.45)",
                                                        animation: "presencePulseRing 2.2s ease-out infinite",
                                                    }}
                                                    aria-hidden="true"
                                                />
                                            )}
                                        </span>
                                        <span
                                            className={[
                                                "mt-3 text-[0.72rem] md:text-[0.82rem] tracking-wide transition-colors duration-500 leading-tight",
                                                state === "active" && "text-ink-goldHi font-medium",
                                                state === "done" && "text-ink-textHigh",
                                                state === "pending" && "text-ink-textLow",
                                            ]
                                                .filter(Boolean)
                                                .join(" ")}
                                        >
                                            {t(key)}
                                        </span>
                                    </li>
                                );
                            })}
                        </ol>

                        {/* ── Progress bar ─────────────────────────────── */}
                        <div
                            className="mt-12 h-[3px] w-full bg-ink-border/60 relative overflow-hidden rounded-full"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={softProgress}
                            data-testid="moment-progress-bar"
                        >
                            <div
                                className="absolute inset-y-0 left-0 bg-gradient-to-r from-ink-gold/70 to-ink-goldHi rounded-full transition-[width] duration-1000 ease-out"
                                style={{
                                    width: `${softProgress}%`,
                                    boxShadow: "0 0 24px rgba(232,192,125,0.35)",
                                }}
                            />
                        </div>

                        {/* ── ETA + elapsed row ───────────────────────────
                            A single subtle row that answers "how long?" and
                            "am I still making progress?" in one glance. */}
                        <div
                            className="mt-6 flex items-center justify-between max-w-xl mx-auto text-xs md:text-sm"
                            data-testid="moment-meta-row"
                        >
                            <span className="text-ink-textMid" data-testid="moment-eta">
                                {t("moment.gen_eta")}
                            </span>
                            <span className="text-ink-textLow tracking-wide tabular-nums" data-testid="moment-elapsed">
                                {Math.floor(elapsed / 60).toString().padStart(2, "0")}:
                                {(elapsed % 60).toString().padStart(2, "0")}
                            </span>
                        </div>

                        {/* ── Reassurance line ────────────────────────── */}
                        <p
                            className="mt-10 text-ink-textLow text-xs md:text-sm italic font-serif max-w-md mx-auto leading-relaxed"
                            data-testid="moment-stay-tag"
                        >
                            {t("moment.gen_stay_tag")}
                        </p>
                    </div>
                )}

                {status === "ready" && (
                    <div data-testid="moment-ready">
                        <p className="label-eyebrow text-ink-gold mb-4">{t("moment.eyebrow")}</p>
                        <h1 className="h-serif text-3xl md:text-4xl leading-tight mb-8">
                            {t("moment.status_ready")}
                        </h1>

                        {videoSrc && (
                            <video
                                ref={videoRef}
                                src={videoSrc}
                                poster={thumbSrc || undefined}
                                controls
                                playsInline
                                preload="metadata"
                                className="w-full rounded-sm border border-ink-border shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)]"
                                data-testid="moment-video"
                            />
                        )}

                        {audioSrc && (
                            <div className="mt-6 text-left">
                                {moment.audio_text && (
                                    <p className="font-serif italic text-ink-textHigh text-xl leading-relaxed mb-3">
                                        "{moment.audio_text}"
                                    </p>
                                )}
                                <audio
                                    src={audioSrc}
                                    controls
                                    preload="metadata"
                                    className="w-full"
                                    data-testid="moment-audio"
                                />
                            </div>
                        )}

                        <div
                            className="mt-12 flex flex-wrap items-center justify-center gap-3 md:gap-4"
                            data-testid="moment-ready-actions"
                        >
                            {videoSrc && (
                                <button
                                    type="button"
                                    onClick={watchNow}
                                    className="btn-primary"
                                    data-testid="moment-watch-now"
                                >
                                    {t("moment.watch_now")}
                                </button>
                            )}
                            {videoSrc && (
                                <button
                                    type="button"
                                    onClick={downloadVideo}
                                    className="btn-secondary"
                                    data-testid="moment-download"
                                >
                                    {t("moment.download")}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => nav(`/presence/${moment.presence_id}/moment`)}
                                className="btn-ghost"
                                data-testid="moment-create-another"
                            >
                                {t("moment.another")}
                            </button>
                        </div>

                        {/* Elegant disclaimer — always present on ready moments. */}
                        <p
                            className="mt-14 text-ink-textLow text-xs md:text-[0.8rem] leading-relaxed italic font-serif max-w-md mx-auto"
                            data-testid="moment-disclaimer"
                        >
                            {t("moment.disclaimer")}
                        </p>
                    </div>
                )}

                {status === "failed" && (
                    <div data-testid="moment-failed">
                        {/* Intentionally NOT using text-ink-danger here — this
                            isn't a "failure", it's "a little more time". The
                            eyebrow uses gold like all other states so the user
                            doesn't read red/alarm on a premium screen. */}
                        <p className="label-eyebrow text-ink-gold mb-6">{t("moment.eyebrow")}</p>
                        <h1 className="h-serif text-3xl md:text-4xl leading-tight">
                            {t("moment.status_failed")}
                        </h1>

                        <p className="mt-6 text-ink-textHigh text-lg leading-relaxed max-w-md mx-auto">
                            {t("moment.failed_reassure")}
                        </p>

                        {/* ── Primary CTA: in-app retry (no new charge, no
                            wizard). Flips the view back to generating the
                            moment the user clicks. ───────────────────────── */}
                        <div className="mt-10 flex flex-wrap items-center justify-center gap-3 md:gap-4">
                            <button
                                type="button"
                                onClick={retryMoment}
                                disabled={isRetrying}
                                className="btn-primary disabled:opacity-60"
                                data-testid="moment-retry"
                            >
                                {isRetrying ? t("moment.failed_retrying") : t("moment.failed_retry")}
                            </button>
                            <Link
                                to={`/chat/${moment?.presence_id || ""}`}
                                className="btn-secondary"
                                data-testid="moment-chat-cta"
                            >
                                {t("moment.failed_chat_cta")}
                            </Link>
                            <Link
                                to={`/presence/${moment?.presence_id || ""}`}
                                className="btn-ghost"
                                data-testid="moment-later-cta"
                            >
                                {t("moment.failed_later_cta")}
                            </Link>
                        </div>

                        {/* ── Causes explainer — discreet, educational, not
                            alarming. Teaches the user WHY this can happen
                            so they feel respected, not confused. ────────── */}
                        <div
                            className="mt-14 max-w-md mx-auto text-left"
                            data-testid="moment-failed-why"
                        >
                            <p className="label-eyebrow text-ink-textLow mb-3">
                                {t("moment.failed_why_title")}
                            </p>
                            <ul className="space-y-2.5 text-sm md:text-[0.9rem] text-ink-textMid leading-relaxed">
                                <li className="flex gap-3">
                                    <span className="text-ink-gold mt-1.5 flex-shrink-0">·</span>
                                    <span>{t("moment.failed_cause_1")}</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-ink-gold mt-1.5 flex-shrink-0">·</span>
                                    <span>{t("moment.failed_cause_2")}</span>
                                </li>
                                <li className="flex gap-3">
                                    <span className="text-ink-gold mt-1.5 flex-shrink-0">·</span>
                                    <span>{t("moment.failed_cause_3")}</span>
                                </li>
                            </ul>
                        </div>

                        <a
                            href="mailto:help@otherside-app.com?subject=Other%20Side%20%E2%80%94%20help"
                            className="mt-10 inline-block text-xs md:text-sm text-ink-textLow italic font-serif hover:text-ink-goldHi transition-colors tracking-wide"
                            data-testid="moment-support-link"
                        >
                            {t("moment.failed_support")} →
                        </a>
                    </div>
                )}

                {err && <p className="text-ink-danger font-serif italic mt-6">{err}</p>}
            </main>
        </div>
    );
}
